import { html, classes } from '../core/html.js';
import { t } from '../core/i18n.js';
import { routeHref } from '../core/url.js';
import { page } from '../components/chrome.js';
import { artwork } from '../components/artwork.js';
import { ctaSection } from '../components/ctaSection.js';
import { processSection } from '../components/processSection.js';
import { icon } from '../components/icon.js';
import { featuredGame, games, releasedGames } from '../data/games.js';
import { services } from '../data/services.js';
import { projects } from '../data/projects.js';

const pad = (value) => String(value).padStart(2, '0');

const selectedSlugs = ['neon-syntax', 'cosmic-synthesis', 'shadow-node', 'power-idle'];
const selectedGames = selectedSlugs
  .map((slug) => games.find((game) => game.slug === slug))
  .filter(Boolean);

/* Read at render time so the ticker follows the active locale during prerendering. */
const tickerItems = () => [
  ...services.map((service) => t(`services.${service.slug}.title`)),
  ...projects.map((project) => t(`lab.${project.slug}.kind`)),
  'iOS',
  'Android',
  'PC',
  'Stralsund',
];

const platforms = [...new Set(games.flatMap((game) => game.platforms))];

/* The stage mixes the lab and the catalogue: Mira in front, Helix and the
   featured game behind it. */
const hero = () => {
  const lead = projects.find((project) => project.slug === 'mira') ?? projects[0];
  const second = projects.find((project) => project !== lead);
  const items = tickerItems();

  return html`
    <section class="hero" aria-labelledby="hero-title">
      <div class="shell hero__grid">
        <div class="hero__copy">
          <p class="hero__kicker">${t('home.hero.eyebrow')}</p>
          <h1 class="hero__title" id="hero-title">
            <span>${t('home.hero.titleTop')}</span>
            <span class="hero__title-em">${t('home.hero.titleAccent')}</span>
          </h1>
          <p class="hero__lede">${t('home.hero.lede')}</p>
          <div class="hero__actions">
            <a class="btn btn--ink btn--lg" href="${routeHref('/lab')}">
              ${t('home.hero.primary')}${icon('arrowRight', { size: 17 })}
            </a>
            <a class="btn btn--outline btn--lg" href="${routeHref('/contact')}">${t('home.hero.secondary')}</a>
          </div>
          <div class="hero__status">
            <span>${t('home.hero.live', { count: pad(releasedGames.length) })}</span>
            <span>${t('home.hero.lab', { count: pad(projects.length) })}</span>
            <span>${platforms.join(' · ')}</span>
          </div>
        </div>

        <div class="hero__stage">
          <figure class="hero__poster hero__poster--back" aria-hidden="true">
            ${artwork(featuredGame.art, '', { sizes: '(max-width: 999px) 42vw, 22vw' })}
          </figure>
          <figure class="hero__poster hero__poster--front" aria-hidden="true">
            ${artwork(second.art, '', { sizes: '(max-width: 999px) 30vw, 15vw' })}
          </figure>
          <figure class="hero__poster hero__poster--main">
            ${artwork(lead.art, t(`lab.${lead.slug}.alt`), {
              sizes: '(max-width: 999px) 88vw, 44vw',
              priority: true,
            })}
          </figure>
          <p class="hero__sticker">
            <span>${t(`state.${lead.state}`)} · ${t(`lab.${lead.slug}.kind`)}</span>
            <strong>${lead.name}</strong>
          </p>
        </div>
      </div>

      <div class="ticker" aria-hidden="true">
        <div class="ticker__track">
          ${[...items, ...items].map(
            (item) => html`<span class="ticker__item">${item}</span>`,
          )}
        </div>
      </div>
    </section>
  `;
};

const studio = () => html`
  <section class="section studio" id="studio" aria-labelledby="studio-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">01</span>
        <span>${t('home.studio.label')}</span>
      </div>
      <div class="studio__grid">
        <h2 class="studio__statement" id="studio-title">
          ${t('home.studio.statement1').trim()} <em>${t('home.studio.statement2')}</em>
        </h2>
        <div class="studio__body">
          <p>${t('home.studio.text1')}</p>
          <p>${t('home.studio.text2')}</p>
        </div>
      </div>
      <div class="stats">
        <div class="stats__item">
          <p class="stats__value">${pad(releasedGames.length)}</p>
          <p class="stats__label">${t('home.stats.titles')}</p>
        </div>
        <div class="stats__item">
          <p class="stats__value">${pad(projects.length)}</p>
          <p class="stats__label">${t('home.stats.lab')}</p>
        </div>
        <div class="stats__item">
          <p class="stats__value">${pad(services.length)}</p>
          <p class="stats__label">${t('home.stats.disciplines')}</p>
        </div>
        <div class="stats__item">
          <p class="stats__value">${pad(platforms.length)}</p>
          <p class="stats__label">${t('home.stats.platforms')}</p>
        </div>
      </div>
    </div>
  </section>
`;

const poster = (game, position) => {
  const title = t(`game.${game.slug}.title`);
  const wide = position % 2 === 0;

  return html`
    <a
      class="${classes('poster', wide ? 'poster--wide' : 'poster--narrow')}"
      href="${routeHref('/games')}"
      style="--accent: ${game.accent}"
      data-reveal
    >
      <div class="poster__frame">
        <div class="poster__art">${artwork(game.art, title, { sizes: '(max-width: 759px) 100vw, 50vw' })}</div>
        <div class="poster__badges">
          <span class="poster__chip">${String(games.indexOf(game) + 1).padStart(2, '0')}</span>
          <span class="${classes('poster__chip', 'poster__chip--state', game.upcoming && 'poster__chip--upcoming')}">
            ${game.upcoming ? t('state.upcoming') : t('state.live')}
          </span>
        </div>
      </div>
      <div class="poster__meta">
        <div>
          <h3 class="poster__title">${title}</h3>
          <p class="poster__sub">${t(game.genreKey)} · ${game.platforms.join(' · ')}</p>
        </div>
        <span class="poster__arrow" aria-hidden="true">${icon('arrowRight', { size: 17 })}</span>
      </div>
    </a>
  `;
};

const labCard = (project, position) => html`
  <a
    class="${classes('poster', position % 2 === 0 ? 'poster--wide' : 'poster--narrow')}"
    href="${routeHref('/lab')}"
    style="--accent: ${project.accent}"
    data-reveal
  >
    <div class="poster__frame">
      <div class="poster__art">
        ${artwork(project.art, t(`lab.${project.slug}.alt`), { sizes: '(max-width: 759px) 100vw, 50vw' })}
      </div>
      <div class="poster__badges">
        <span class="poster__chip">${pad(position + 1)}</span>
        <span class="poster__chip poster__chip--state poster__chip--upcoming">${t(`state.${project.state}`)}</span>
      </div>
    </div>
    <div class="poster__meta">
      <div>
        <h3 class="poster__title">${project.name}</h3>
        <p class="poster__sub">${t(`lab.${project.slug}.kind`)}</p>
      </div>
      <span class="poster__arrow" aria-hidden="true">${icon('arrowRight', { size: 17 })}</span>
    </div>
  </a>
`;

const lab = () => html`
  <section class="section" id="lab" aria-labelledby="lab-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">02</span>
        <span>${t('home.lab.label')}</span>
      </div>
      <div class="works__head">
        <h2 class="works__title" id="lab-title">${t('home.lab.title')}</h2>
        <p class="works__lede">${t('home.lab.lede')}</p>
      </div>
      <div class="poster-grid">
        ${projects.map((project, index) => labCard(project, index))}
      </div>
      <a class="link-arrow works__all" href="${routeHref('/lab')}">
        ${t('home.lab.all')}${icon('arrowRight', { size: 16 })}
      </a>
    </div>
  </section>
`;

const works = () => html`
  <section class="section works" id="arbeiten" aria-labelledby="works-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">03</span>
        <span>${t('home.works.label')}</span>
      </div>
      <div class="works__head">
        <h2 class="works__title" id="works-title">${t('home.works.title')}</h2>
        <p class="works__lede">${t('home.works.lede')}</p>
      </div>
      <div class="poster-grid">
        ${selectedGames.map((game, index) => poster(game, index))}
      </div>
      <a class="link-arrow works__all" href="${routeHref('/games')}">
        ${t('home.works.all')}${icon('arrowRight', { size: 16 })}
      </a>
    </div>
  </section>
`;

const serviceLinks = {
  '/games': 'services.link.games',
  '/contact': 'services.link.contact',
};

const servicesSection = () => html`
  <section class="section" id="leistungen" aria-labelledby="services-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">04</span>
        <span>${t('home.services.label')}</span>
      </div>
      <div class="section-head">
        <h2 class="section-head__title" id="services-title">${t('home.services.title')}</h2>
        <p class="section-head__lede">${t('home.services.lede')}</p>
      </div>
      <div class="service-list">
        ${services.map(
          (service, index) => html`
            <a class="service-row" href="${routeHref(service.link)}" data-reveal>
              <span class="service-row__num" aria-hidden="true">${String(index + 1).padStart(2, '0')}</span>
              <span class="service-row__icon" aria-hidden="true">${icon(service.icon, { size: 20 })}</span>
              <h3 class="service-row__title">${t(`services.${service.slug}.title`)}</h3>
              <span class="service-row__desc">${t(`services.${service.slug}.description`)}</span>
              <span class="service-row__arrow" aria-hidden="true">${icon('arrowRight', { size: 16 })}</span>
            </a>
          `,
        )}
      </div>
      <p class="section-foot">
        <a class="link-arrow" href="${routeHref('/services')}">
          ${t('home.services.all')}${icon('arrowRight', { size: 16 })}
        </a>
      </p>
    </div>
  </section>
`;

export const HomePage = () => page(html`
  ${hero()}${studio()}${lab()}${works()}${servicesSection()}${processSection({ number: '05' })}${ctaSection()}
`);
